import {Dictionary} from 'lodash';
import {DynamicDialogConfig} from 'primeng/dynamicdialog';
import {Observable} from 'rxjs';

import {Component, Input, ViewChild} from '@angular/core';
import {EDialogOpenMode} from '../../enums/dialog-open-mode.enum';
import {StringUtils} from '../../utils/string.util';
import {IBaseCRUModalContentComponent} from '../ibase-cru-modal-content/ibase-cru-modal-content.component';
import {IBaseModalComponent} from '../ibase-modal/ibase-modal.component';
import {ISubmitRequestMessages} from '../ibase/ibase.component';

/**
 * Dialog parameter
 */
export class ICRUModalDialogParameter<t_ConfigExtended> {
    /**
     * Constructor
     * @param dialogOpenMode Dialog open mode
     * @param duplicate Allow duplicates of this modal
     * @param extendedxtended Extended config
     * @param showGoTo Show go to button
     */
    constructor(
        public dialogOpenMode: EDialogOpenMode,
        public extended?: t_ConfigExtended | null,
        public showGoTo?: boolean,
        public duplicate?: boolean
    ) {}
}

/**
 * Base config
 */
export class ICRUModalConfigBase<t_ConfigExtended> {
    constructor(
        public dialogOpenMode?: EDialogOpenMode,
        public extended?: t_ConfigExtended | null,
        public showGoTo?: boolean
    ) {}
}

/**
 * Base result
 */
export class IBaseCRUModalResultBase<t_ResultExtended> {
    constructor(
        public dialogOpenMode?: EDialogOpenMode,
        public extended?: t_ResultExtended | null
    ) {}
}

/**
 * Default empty extended Config/Result
 */
export class IBaseCRUModalXExtendedNone {}

/**
 * Base Create Read Update modal component
 */
@Component({
    selector: 'app-ibase-cru-modal-content',
    template: '',
    standalone: true
})
export abstract class IBaseCRUModalComponent<
    t_Model,
    t_Component,
    t_ConfigExtended,
    t_ResultExtended
> extends IBaseModalComponent<t_Component, ICRUModalConfigBase<t_ConfigExtended>> {
    protected readonly Object = Object;
    protected readonly EDialogOpenMode = EDialogOpenMode;

    // ===============================================================
    // ===============================================================

    protected getModelHumanIdentifier!: (model: t_Model) => string;

    protected routeParams: Dictionary<string> = {};

    @ViewChild(IBaseCRUModalContentComponent, {static: true})
    protected iBaseCRUModalContent!: IBaseCRUModalContentComponent;

    // ===============================================================
    // ===============================================================

    @Input()
    public canSubmitExtra?: () => boolean;
    @Input()
    public canRevert?: () => boolean;
    @Input()
    public goTo?: () => void;

    public contentMinHeight = '5rem';
    public modelName!: string;
    public modelHumanName!: string;
    public dialogOpenMode: EDialogOpenMode = EDialogOpenMode.READ_ONLY;
    public isCreationMode!: boolean;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.dialogOpenMode = this.config.data?.dialogOpenMode ?? EDialogOpenMode.READ_ONLY;
        this.isCreationMode =
            this.dialogOpenMode === EDialogOpenMode.NEW || this.dialogOpenMode === EDialogOpenMode.CLONE;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Configure the modal
     * @param modelName Name of the model
     * @param getModelHumanIdentifier Model instance human identifier getter
     */
    protected configure(modelName: string, getModelHumanIdentifier: (model: t_Model) => string) {
        this.modelName = modelName;

        this.modelHumanName = StringUtils.toHumanFormatedStr(this.modelName.replace('ApiModel', ''));

        this.getModelHumanIdentifier = getModelHumanIdentifier;

        this.updateModalTitle();

        this.config.draggable = true;
        this.config.maximizable = true;
        this.config.resizable = true;
        this.config.keepInViewport = true;
    }
    /**
     * Update the modal title
     */
    protected updateModalTitle(version?: string) {
        switch (this.dialogOpenMode) {
            case EDialogOpenMode.NEW:
            case EDialogOpenMode.CLONE:
                this.config.header = `Create new ${this.modelHumanName.toLowerCase()}`;
                break;

            case EDialogOpenMode.EDIT:
                this.config.header = `Edit ${this.modelHumanName.toLowerCase()}`;
                break;

            case EDialogOpenMode.READ_ONLY:
            case EDialogOpenMode.REVERT:
                this.config.header = `View ${this.modelHumanName.toLowerCase()}`;
                break;
        }
        if (version) {
            this.config.header += ` (${version})`;
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * Get dialog service parameters
     * @param options Configuration
     * @returns OO8ModalConfigBase<t_ConfigExtended>
     */
    public static getDialogServiceParameters<t_ConfigExtended>(
        options: ICRUModalDialogParameter<t_ConfigExtended>
    ): DynamicDialogConfig<ICRUModalConfigBase<t_ConfigExtended>> {
        return {
            modal: true,
            header: '',
            duplicate: options.duplicate ?? false,
            data: new ICRUModalConfigBase<t_ConfigExtended>(options.dialogOpenMode, options.extended, options.showGoTo)
        };
    }

    // ===============================================================
    // ===============================================================

    /**
     * On init
     */
    public override async ngOnInit() {
        if (!this.modelName) {
            this.globalsService.messageService.add({
                key: this.overrideMessagesKey,
                severity: 'error',
                detail: 'Error, this component is not configured!'
            });

            return;
        }

        this.loadingStr = 'Initializing...';
        this.iBaseCRUModalContent.iBaseCRUModal = this;
        this.iBaseCRUModalContent.showGoTo = this.goTo !== undefined && this.config.data?.showGoTo === true;

        await super.ngOnInit();

        if (this.dialogOpenMode === EDialogOpenMode.READ_ONLY) {
            this.form?.disable();
        }
    }

    // ===============================================================
    // ===============================================================

    /**
     * On cancel
     */
    public onCancel() {
        this.ref.close();
    }
    /**
     * On previous
     */
    public onPrevious() {
        // No default implementation
    }
    /**
     * On next
     */
    public onNext() {
        // No default implementation
    }

    // ===============================================================
    // ===============================================================

    /**
     * Can the modal be submitted ?
     * @param checkForm Should check form validity if any
     * @returns True if the modal can be submitted
     */
    public canSubmit(checkForm = true): boolean {
        if (this.dialogOpenMode == EDialogOpenMode.READ_ONLY) {
            return false;
        }
        if (checkForm && this.form && !this.form.valid) {
            return false;
        }

        if (this.canSubmitExtra && !this.canSubmitExtra()) {
            return false;
        }

        if (this.dialogOpenMode == EDialogOpenMode.REVERT && this.canRevert && !this.canRevert()) {
            return false;
        }
        return true;
    }

    protected getSubmitRequestMessages(): ISubmitRequestMessages<t_Model> {
        return {
            loading: `${this.isCreationMode ? 'Creating' : 'Updating'} ${this.modelHumanName.toLowerCase()}...`,
            successSummary: `${this.isCreationMode ? 'Created' : 'Updated'} ${this.modelHumanName.toLowerCase()}`,
            errorSummary: `${this.isCreationMode ? 'Failed to create' : 'Failed to update'} ${this.modelHumanName.toLowerCase()}`,
            successDetail: (result: t_Model) => this.getModelHumanIdentifier(result),
            errorDetail: (err: unknown) => (err as {error?: {message?: string}})?.error?.message ?? 'Unknown'
        };
    }

    /**
     * Generic submit request method. Closes the modal on success with the result as extended result
     */
    protected override async submitRequest<t_Model, t_ResultExtended>(
        serviceFunction: () => Observable<t_Model>,
        messages: ISubmitRequestMessages<t_Model>,
        extendedResultBuilder?: ((resultCRUD: t_Model) => t_ResultExtended) | null
    ): Promise<t_ResultExtended | null> {
        const result = await super.submitRequest(serviceFunction, messages, extendedResultBuilder);
        if (result) {
            this.ref.close(new IBaseCRUModalResultBase<t_ResultExtended>(this.dialogOpenMode, result));
        }
        return result;
    }

    /**
     * Generic submit method for Create/Update entities
     * @param service Service instance
     * @param createService Post/Create method
     * @param updateService Put/Update method
     * @param uuid Existing UUID if any of the model
     * @param payload Payload to submit of type @t_CreatePayload or @t_UpdatePayload
     * @param extendedResultBuilder Extended result builder function
     */
    protected async submitCU<t_CreatePayload, t_UpdatePayload>(
        service: unknown,
        createService: (payload: t_CreatePayload) => Observable<t_Model>,
        updateService: (uuid: string, payload: t_UpdatePayload) => Observable<t_Model>,
        uuid: string | undefined,
        payload: t_CreatePayload | t_UpdatePayload,
        extendedResultBuilder: (resultCRUD: t_Model) => t_ResultExtended
    ) {
        const messages = this.getSubmitRequestMessages();
        const result = await this.submitRequest(
            () =>
                this.isCreationMode
                    ? createService.bind(service)(payload as t_CreatePayload)
                    : updateService.bind(service)(uuid!, payload as t_UpdatePayload),
            messages,
            extendedResultBuilder
        );
        if (result) {
            this.ref.close(new IBaseCRUModalResultBase<t_ResultExtended>(this.dialogOpenMode, result));
        }
    }
}
